import { Refresh } from '@mui/icons-material';
import 'ckeditor5/ckeditor5.css';
import React, { useCallback, useEffect, useState } from 'react';
import { Footer } from '../../components/Footer/Footer.tsx';
import { Header } from '../../components/Header/Header.tsx';
import Hero from '../../components/Hero/Hero.tsx';
import { GroupMemberRoleView, LessonDifficultyLevelView } from '../../graphql/types.gen.ts';
import { useIsUserLoggedIn } from '../../helpers/useValidateToken.ts';
import { GENERAL_GROUP_NAME } from '../../index.tsx';
import { useLastGroupQuery } from '../FormPage/LoginPage/loginPage.gen.ts';
import { LessonItem } from './LessonItem/LessonItem.tsx';
import './Lessons.scss';
import {
	useGetLessonsFromGroupLazyQuery,
	useGetLessonsFromPublicLazyQuery,
	useLessonOrderChangeMutation,
} from './lessons.gen.ts';

const DIFFICULTY_GROUPS: Array<{ key: LessonDifficultyLevelView; title: string }> = [
	{
		key: LessonDifficultyLevelView.Beginner,
		title: 'Początkujący',
	},
	{
		key: LessonDifficultyLevelView.Intermediate,
		title: 'Średniozaawansowany',
	},
	{
		key: LessonDifficultyLevelView.Advanced,
		title: 'Zaawansowany',
	},
];
type LessonStatus = 'unavailable' | 'done' | 'available';

type LessonListItem = {
	slug: string;
	title: string;
	hasHomework?: boolean;
	isHomeworkDone?: boolean;
	status: LessonStatus;
};

type GroupedLessonSection = {
	key: LessonDifficultyLevelView;
	title: string;
	items: LessonListItem[];
};

export const Lessons = () => {
	const { isUserLoggedIn } = useIsUserLoggedIn();
	const { data: chosenGroupData } = useLastGroupQuery({ skip: !isUserLoggedIn });
	const chosenGroup = chosenGroupData?.me?.recentlyUsedGroupId || GENERAL_GROUP_NAME;
	const [lessonOrderChangeMutation] = useLessonOrderChangeMutation();

	const [groupedLessons, setGroupedLessons] = useState<GroupedLessonSection[]>([]);
	const [isLoadingLessons, setIsLoadingLessons] = useState<boolean>(false);
	const [hasError, setHasError] = useState<boolean>(false);
	const [isInstructor, setIsInstructor] = useState<boolean>(false);

	const [getLessons, { loading: isLoadingPrivateLessons, error: privateError }] = useGetLessonsFromGroupLazyQuery({
		variables: {
			lessonsInput2: {
				filters: {
					difficultyLevel: [],
					tags: [],
				},
				pagination: {
					limit: 50,
					offset: 0,
				},
			},
			code: chosenGroup,
		},
		fetchPolicy: 'cache-and-network',
	});
	const [getPublicLessons, { loading: isLoadingPublicLessons, error: publicError }] =
		useGetLessonsFromPublicLazyQuery({
			variables: {
				input: {
					filters: {
						difficultyLevel: [],
						tags: [],
					},
					pagination: {
						limit: 50,
						offset: 0,
					},
				},
			},
		});
	const fetchGroupedLessons = useCallback(async () => {
		setIsLoadingLessons(true);
		setHasError(false);

		try {
			const groupedResponses = await Promise.all(
				DIFFICULTY_GROUPS.map(async (group) => {
					if (isUserLoggedIn) {
						const { data } = await getLessons({
							variables: {
								lessonsInput2: {
									filters: {
										difficultyLevel: [group.key],
										tags: [],
									},
									pagination: {
										limit: 50,
										offset: 0,
									},
								},
								code: chosenGroup,
							},
						});

						return {
							key: group.key,
							title: group.title,
							role: data?.group?.me?.role,
							items: data?.group?.lessons?.items ?? [],
						};
					}

					const { data } = await getPublicLessons({
						variables: {
							input: {
								filters: {
									difficultyLevel: [group.key],
									tags: [],
								},
								pagination: {
									limit: 50,
									offset: 0,
								},
							},
						},
					});

					return {
						key: group.key,
						title: group.title,
						role: null,
						items: data?.publicGroups[0]?.lessons?.items ?? [],
					};
				})
			);

			if (isUserLoggedIn) {
				setIsInstructor(groupedResponses.some((response) => response.role === GroupMemberRoleView.Instructor));
			} else {
				setIsInstructor(false);
			}

			const allLessons = groupedResponses.flatMap((response) => response.items);
			let latestDoneIndex = -1;

			if (isUserLoggedIn && allLessons.length) {
				for (let i = 0; i < allLessons.length; i++) {
					const lesson = allLessons[i];

					const isHomeworkDone = 'isHomeworkDone' in lesson ? lesson.isHomeworkDone : false;
					const hasHomework = 'hasHomework' in lesson ? lesson.hasHomework : true;

					if (i === 0 || i === latestDoneIndex + 1) {
						if (isHomeworkDone || !hasHomework) {
							latestDoneIndex = i;
						} else {
							break;
						}
					} else {
						break;
					}
				}
			} else if (allLessons.length) {
				latestDoneIndex = allLessons.length - 1;
			}

			let flatIndex = 0;
			const sectionsWithStatus = groupedResponses.reduce<GroupedLessonSection[]>((acc, response) => {
				if (!response.items.length) {
					return acc;
				}

				const items: LessonListItem[] = response.items.map((lesson) => {
					let status: LessonStatus = 'unavailable';

					if (flatIndex <= latestDoneIndex || lesson.hasHomework === false) {
						status = 'done';
					} else if (flatIndex === latestDoneIndex + 1) {
						status = 'available';
					}

					flatIndex += 1;

					return {
						slug: lesson.slug,
						title: lesson.title,
						hasHomework: 'hasHomework' in lesson ? lesson.hasHomework : undefined,
						isHomeworkDone: 'isHomeworkDone' in lesson ? lesson.isHomeworkDone : undefined,
						status,
					};
				});

				acc.push({
					key: response.key,
					title: response.title,
					items,
				});

				return acc;
			}, []);

			setGroupedLessons(sectionsWithStatus);
		} catch {
			setHasError(true);
			setGroupedLessons([]);
		} finally {
			setIsLoadingLessons(false);
		}
	}, [chosenGroup, getLessons, getPublicLessons, isUserLoggedIn]);

	useEffect(() => {
		fetchGroupedLessons();
	}, [fetchGroupedLessons]);

	const handleOrderChange = async (direction: 'forward' | 'back', index, sectionKey: LessonDifficultyLevelView) => {
		const sectionLessons = groupedLessons.find((group) => group.key === sectionKey)?.items ?? [];
		const lessonSlug = sectionLessons[index]?.slug;

		if (!lessonSlug) {
			return;
		}

		await lessonOrderChangeMutation({
			variables: {
				input: {
					groupCode: chosenGroup,
					lessonToMoveSlug: lessonSlug,
					moveBeforeLessonSlug: sectionLessons[direction === 'forward' ? index + 2 : index - 1]?.slug || null,
				},
			},
		});

		fetchGroupedLessons();
	};

	if (
		(hasError || privateError || publicError) &&
		!isLoadingLessons &&
		!isLoadingPrivateLessons &&
		!isLoadingPublicLessons
	) {
		return <div>Ups, wystąpił błąd, spróbuj odświeżyć stronę</div>;
	}

	return (
		<div className="lessons light">
			<div className="content">
				<Header light={true} />
				<Hero title="Nauka" />
				<div className="lessons__container lessons__container--index container">
					<h2 className="lessons__title">Lista lekcji</h2>
					<p className="lessons__noLessons">
						Aby przejść do kolejnej lekcji, musisz ukończyć testy w poprzedniej.
					</p>
					{!isInstructor && !groupedLessons.length && !isLoadingLessons && (
						<p className="lessons__noLessons">W tej grupie nie ma lekcji</p>
					)}
					<div className="lessons__items">
						{isLoadingLessons ? (
							<p className="lessons__loading" aria-busy="true">
								<Refresh className="allTests__spinner" />
								<p>Ładowanie lekcji...</p>
							</p>
						) : (
							groupedLessons.map((group) => (
								<section className="lessons__section" key={group.key}>
									<div className="lessons__sectionHeader">
										<div className="lessons__sectionLine" />
										<h3 className="lessons__sectionTitle">{group.title}</h3>
										<div className="lessons__sectionLine" />
									</div>
									<div className="lessons__sectionItems">
										{group.items.map((lesson, index) => {
											const isFirstInSection = index === 0;
											const isLastInSection = index === group.items.length - 1;
											const handleMoveBack = isFirstInSection
												? undefined
												: () => handleOrderChange('back', index, group.key);
											const handleMoveForward = isLastInSection
												? undefined
												: () => handleOrderChange('forward', index, group.key);

											return (
												<LessonItem
													isInstructor={isInstructor}
													key={lesson.title}
													title={lesson.title}
													url={`/${chosenGroup}/lekcja/${lesson.slug}`}
													status={lesson.status}
													handleMoveBack={handleMoveBack}
													handleMoveForward={handleMoveForward}
												/>
											);
										})}
									</div>
								</section>
							))
						)}
						{isInstructor && (
							<div className="lessons__addLessonWrapper">
								<LessonItem title="Dodaj lekcję" add url="/dodaj-lekcje" />
							</div>
						)}
					</div>
				</div>
			</div>
			<Footer />
		</div>
	);
};
